import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useProduct } from "../../context/ProductContext";
import "./Cart.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLocalContext } from "../../context/LocalContext";
import Products from "../product/Products";
import WhyChooseUs from "../WhyChooseUs/WhyChooseUs";
import Disclaimer from "../T&C/Disclaimer";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faBolt,
  faHeadset,
  faMinus,
  faPlus,
  faTrash,
  faShieldHalved,
  faCreditCard,
  faTag,
  faTimes,
  faCartShopping,
  faBroom,
  faGift,
  faCircleCheck,
  faStar,
  faReceipt,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCcVisa,
  faCcMastercard,
  faPaypal,
} from "@fortawesome/free-brands-svg-icons";

/** Config **/
const COUPONS = {
  SAVE50: { type: "percent", value: 50, label: "SAVE50 • 50% OFF" },
  SAVE10: { type: "percent", value: 10, label: "SAVE10 • 10% OFF" },
  FLAT5: { type: "flat", value: 5, label: "FLAT5 • $5 OFF" },
};

const TAX_RATE = 0;
const PERK_THRESHOLD = 149;

function formatCurrency(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

const Cart = () => {
  const { cart, setCart } = useProduct();
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const navigate = useNavigate();
  const { webinfo = {} } = useLocalContext();
  const brandName = webinfo.name || "our store";

  useEffect(() => {
    document.title = `Cart | ${webinfo.name || "Store"}`;
  }, [webinfo?.name]);

  useEffect(() => {
    try {
      localStorage.setItem("cart_items", JSON.stringify(cart || []));
    } catch {}
  }, [cart]);

  const normalizedCart = useMemo(
    () =>
      (cart || []).map((i) => ({
        ...i,
        qty: Math.max(1, Number(i.qty) || 1),
      })),
    [cart]
  );

  const subtotal = useMemo(() => {
    const sum = normalizedCart.reduce(
      (acc, it) => acc + parseFloat(it.price || 0) * it.qty,
      0
    );
    return Number(sum.toFixed(2));
  }, [normalizedCart]);

  const discountAmount = useMemo(() => {
    if (!appliedCoupon) return 0;
    const { type, value } = appliedCoupon;
    if (type === "percent") return Number(((subtotal * value) / 100).toFixed(2));
    if (type === "flat") return Math.min(subtotal, Number(Number(value).toFixed(2)));
    return 0;
  }, [appliedCoupon, subtotal]);

  const taxable = useMemo(
    () => Math.max(0, subtotal - discountAmount),
    [subtotal, discountAmount]
  );

  const tax = useMemo(() => Number((taxable * TAX_RATE).toFixed(2)), [taxable]);

  const total = useMemo(
    () => Number(Math.max(0, taxable + tax).toFixed(2)),
    [taxable, tax]
  );

  const progressPct = Math.min(100, Math.round((taxable / PERK_THRESHOLD) * 100));

  const handleRemove = useCallback(
    (itemId) => {
      setCart((prev) => prev.filter((item) => item.id !== itemId));
      toast.info("Removed from cart.");
    },
    [setCart]
  );

  const setQty = useCallback(
    (itemId, nextQty) => {
      const q = Math.max(1, Number(nextQty) || 1);
      setCart((prev) => prev.map((it) => (it.id === itemId ? { ...it, qty: q } : it)));
    },
    [setCart]
  );

  const inc = useCallback(
    (itemId) => {
      setCart((prev) =>
        prev.map((it) =>
          it.id === itemId ? { ...it, qty: Math.max(1, (it.qty || 1) + 1) } : it
        )
      );
    },
    [setCart]
  );

  const dec = useCallback(
    (itemId) => {
      setCart((prev) =>
        prev.map((it) => {
          if (it.id !== itemId) return it;
          return { ...it, qty: Math.max(1, (it.qty || 1) - 1) };
        })
      );
    },
    [setCart]
  );

  const handleApplyCoupon = useCallback(() => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return toast.error("Enter a coupon code.");
    const found = COUPONS[code];

    if (!found) {
      setAppliedCoupon(null);
      return toast.error("Coupon not recognized.");
    }

    setAppliedCoupon(found);
    toast.success(`Applied: ${found.label}`);
  }, [couponCode]);

  const clearCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponCode("");
    toast.info("Coupon removed.");
  }, []);

  const handleProceedToPay = useCallback(() => {
    if (normalizedCart.length > 0) navigate("/payment");
    else toast.warning("Your cart is empty.");
  }, [normalizedCart.length, navigate]);

  const clearCart = useCallback(() => {
    if (normalizedCart.length === 0) return;
    setCart([]);
    toast.info("Cart cleared.");
  }, [normalizedCart.length, setCart]);

  const suggestedCoupons = Object.keys(COUPONS);

  const perkText =
    taxable >= PERK_THRESHOLD
      ? "Unlocked — complimentary remote setup included."
      : `${formatCurrency(Math.max(0, PERK_THRESHOLD - taxable))} away from complimentary remote setup`;

  return (
    <>
      <div className="crt-page">
        {/* HERO */}
        <header className="crt-hero">
          <div className="crt-hero-left">
            <p className="crt-kicker">
              <FontAwesomeIcon icon={faStar} /> Checkout Ready
            </p>

            <h1 className="crt-title">Your cart — clean, fast, and secure</h1>

            <p className="crt-sub">
              Review your plan details and savings. After payment, you’ll receive digital delivery +
              activation guidance instantly.
            </p>

            <div className="crt-trust">
              <div className="crt-trust-card">
                <span className="crt-icobadge">
                  <FontAwesomeIcon icon={faLock} />
                </span>
                <div>
                  <strong>Secure payments</strong>
                  <p>Protected checkout experience</p>
                </div>
              </div>

              <div className="crt-trust-card">
                <span className="crt-icobadge">
                  <FontAwesomeIcon icon={faBolt} />
                </span>
                <div>
                  <strong>Instant delivery</strong>
                  <p>Digital access in minutes</p>
                </div>
              </div>

              <div className="crt-trust-card">
                <span className="crt-icobadge">
                  <FontAwesomeIcon icon={faHeadset} />
                </span>
                <div>
                  <strong>Human support</strong>
                  <p>Help with install & activation</p>
                </div>
              </div>
            </div>
          </div>

          {/* PERK */}
          <div className="crt-perk">
            <div className="crt-perk-top">
              <div className="crt-perk-title">
                <FontAwesomeIcon icon={faGift} /> Remote setup perk
              </div>

              <div className="crt-perk-msg">
                {taxable >= PERK_THRESHOLD ? (
                  <span className="crt-good">
                    <FontAwesomeIcon icon={faCircleCheck} /> {perkText}
                  </span>
                ) : (
                  <span className="crt-muted">{perkText}</span>
                )}
              </div>
            </div>

            <div
              className="crt-perk-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPct}
            >
              <div className="crt-perk-fill" style={{ width: `${progressPct}%` }} />
            </div>

            <div className="crt-perk-foot">
              <span>
                Threshold: <strong>{formatCurrency(PERK_THRESHOLD)}</strong>
              </span>
              <span>
                Current: <strong>{formatCurrency(taxable)}</strong>
              </span>
            </div>
          </div>
        </header>

        {/* GRID */}
        <div className="crt-grid">
          {/* ITEMS */}
          <section className="crt-items" aria-label="Cart items">
            <div className="crt-items-head">
              <div className="crt-items-title">
                <FontAwesomeIcon icon={faCartShopping} /> Cart items{" "}
                <span className="crt-count">
                  {normalizedCart.length === 0 ? "(0)" : `(${normalizedCart.length})`}
                </span>
              </div>

              {normalizedCart.length > 0 && (
                <button className="crt-ghost" onClick={clearCart}>
                  <FontAwesomeIcon icon={faBroom} /> Clear
                </button>
              )}
            </div>

            {normalizedCart.length === 0 ? (
              <div className="crt-empty">
                <div className="crt-empty-ico">
                  <FontAwesomeIcon icon={faCartShopping} />
                </div>
                <h3>Your cart is empty</h3>
                <p>
                  Browse plans and add what you need — we’ll keep everything ready for checkout.
                </p>
                <button className="crt-primary" onClick={() => navigate("/norton")}>
                  Explore plans <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            ) : (
              <div className="crt-list">
                {normalizedCart.map((item) => {
                  const lineTotal = Number((parseFloat(item.price || 0) * item.qty).toFixed(2));

                  return (
                    <article className="crt-item" key={item.id}>
                      <div className="crt-item-media">
                        <img src={item.img} alt={item.name} className="crt-img" />
                      </div>

                      <div className="crt-item-body">
                        <div className="crt-item-top">
                          <div className="crt-item-title">
                            <h3>{item.name}</h3>
                            <p className="crt-item-sub">
                              {item.subtitle ||
                                "Digital license key + activation guide delivered after payment."}
                            </p>
                          </div>

                          <div className="crt-item-price">
                            <span className="crt-line">{formatCurrency(lineTotal)}</span>
                            <span className="crt-unit">{formatCurrency(item.price)} / ea</span>
                          </div>
                        </div>

                        <div className="crt-meta">
                          <span className="crt-chip">
                            <FontAwesomeIcon icon={faBolt} /> Instant delivery
                          </span>
                          <span className="crt-chip">
                            <FontAwesomeIcon icon={faShieldHalved} /> Verified license
                          </span>
                        </div>

                        <div className="crt-actions">
                          <div
                            className="crt-qty"
                            role="group"
                            aria-label={`Quantity selector for ${item.name}`}
                          >
                            <button
                              className="crt-qty-btn"
                              onClick={() => dec(item.id)}
                              aria-label={`Decrease ${item.name} quantity`}
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>

                            <input
                              className="crt-qty-input"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={item.qty}
                              onChange={(e) => setQty(item.id, Number(e.target.value || 1))}
                              aria-label={`${item.name} quantity`}
                            />

                            <button
                              className="crt-qty-btn"
                              onClick={() => inc(item.id)}
                              aria-label={`Increase ${item.name} quantity`}
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>

                          <button
                            className="crt-danger"
                            onClick={() => handleRemove(item.id)}
                            aria-label={`Remove ${item.name} from cart`}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Remove
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {/* SUMMARY */}
          <aside className="crt-summary" aria-label="Order summary">
            <div className="crt-card">
              <div className="crt-card-head">
                <h2>
                  <FontAwesomeIcon icon={faReceipt} /> Order summary
                </h2>

                {appliedCoupon ? (
                  <span className="crt-badge">
                    <FontAwesomeIcon icon={faTag} /> {appliedCoupon.label}
                  </span>
                ) : (
                  <span className="crt-badge muted">No coupon</span>
                )}
              </div>

              {/* Coupon */}
              <div className="crt-coupon">
                <div className="crt-coupon-row">
                  <div className="crt-coupon-input">
                    <FontAwesomeIcon icon={faTag} />
                    <input
                      type="text"
                      placeholder="Apply coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                      aria-label="Coupon code"
                    />
                  </div>

                  <button className="crt-apply" onClick={handleApplyCoupon} aria-label="Apply coupon">
                    Apply
                  </button>
                </div>

                <div className="crt-suggest">
                  {suggestedCoupons.map((c) => (
                    <button
                      key={c}
                      className="crt-suggest-chip"
                      onClick={() => setCouponCode(c)}
                      aria-label={`Use coupon ${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>

                {appliedCoupon && (
                  <div className="crt-applied">
                    <span className="crt-applied-tag">
                      <FontAwesomeIcon icon={faTag} /> Discount active
                    </span>
                    <button className="crt-link" onClick={clearCoupon} aria-label="Remove coupon">
                      <FontAwesomeIcon icon={faTimes} /> Remove
                    </button>
                  </div>
                )}
              </div>

              <div className="crt-breakdown">
                <div className="crt-row">
                  <span>Subtotal</span>
                  <strong>{formatCurrency(subtotal)}</strong>
                </div>

                <div className="crt-row">
                  <span>Discount</span>
                  <strong className={discountAmount ? "ok" : "muted"}>
                    {discountAmount ? `− ${formatCurrency(discountAmount)}` : "—"}
                  </strong>
                </div>

                {TAX_RATE > 0 && (
                  <div className="crt-row">
                    <span>Tax</span>
                    <strong>{formatCurrency(tax)}</strong>
                  </div>
                )}

                <div className="crt-divider" />

                <div className="crt-row total">
                  <span>Total</span>
                  <strong>{formatCurrency(total)}</strong>
                </div>

                <p className="crt-help">
                  After payment, activation instructions will be sent to the email provided at checkout.
                </p>
              </div>

              <button
                className="crt-checkout"
                onClick={handleProceedToPay}
                disabled={normalizedCart.length === 0}
              >
                <FontAwesomeIcon icon={faCreditCard} /> Proceed to payment
              </button>

              <div className="crt-payments" aria-label="Accepted payment methods">
                <span>We accept</span>
                <div className="crt-pay-logos">
                  <FontAwesomeIcon icon={faCcVisa} title="Visa" />
                  <FontAwesomeIcon icon={faCcMastercard} title="Mastercard" />
                  <FontAwesomeIcon icon={faPaypal} title="PayPal" />
                </div>
              </div>
            </div>

            <div className="crt-safe">
              <span className="crt-safe-ico" aria-hidden>
                <FontAwesomeIcon icon={faShieldHalved} />
              </span>
              <div>
                <strong>Trusted delivery from {brandName}</strong>
                <p>
                  Licenses are sourced from official channels. If you face any activation issue,
                  we’ll help you fix it quickly.
                </p>
              </div>
            </div>
          </aside>
        </div>

        {/* Mobile sticky */}
        {normalizedCart.length > 0 && (
          <div className="crt-sticky">
            <div className="crt-sticky-left">
              <span>Total</span>
              <strong>{formatCurrency(total)}</strong>
            </div>
            <button onClick={handleProceedToPay} aria-label="Checkout now">
              <FontAwesomeIcon icon={faCreditCard} /> Checkout
            </button>
          </div>
        )}
      </div>

      <Products />
      <WhyChooseUs />
      <Disclaimer />
    </>
  );
};

export default Cart;
